import { Box, IconButton, Skeleton, Typography } from '@mui/material'
import { Icons } from 'utoniq-ui'

import {
  DigitalMembershipCardDetailQuery,
  Maybe,
  MembershipInterval,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { DigitalMembershipCard } from '@/components/organisms/digitalMembershipCard'
import { User as AuthUser, useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'
import { MembershipSubscriptionProduct } from '@/core/membership'

export type State = {
  loading: boolean
  currentUser: Maybe<AuthUser>
  artist: DigitalMembershipCardDetailQuery['artist']
  patron: MembershipSubscriptionProduct | null
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const DigitalMembershipCardDetailTemplate: Template<State, Action> = ({
  state,
}) => {
  const { routerNames, router, routerPages } = useRouter()
  const { t } = useTranslation('digitalMembershipCardDetail')

  const handleClose = () => {
    router.goBack(routerPages.MyProfile)
  }

  return (
    <BaseLayout safeArea={{ pt: 0 }} navigation={undefined}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
        }}
      >
        {/* 左上のバツ印 */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            color: 'text.primary',
          }}
        >
          <Box
            component="span"
            sx={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
            }}
          >
            <Icons.Close fontSize="medium" />
          </Box>
        </IconButton>

        <DigitalMembershipCardContent
          loading={state.loading}
          artist={state.artist}
          currentUser={state.currentUser}
          patron={state.patron}
        />

        {/* 左下のメンバーシップ管理へのリンク */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            color: 'primary.main',
          }}
          onClick={() => router.push(routerPages.MembershipManagement)}
        >
          <Icons.MoveIn fontSize="medium" sx={{ width: 24, height: 24 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: '10px',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {t('membershipManagement')}
          </Typography>
        </Box>
      </Box>
    </BaseLayout>
  )
}

type DigitalMembershipCardDetailProps = {
  loading: boolean
  artist: State['artist']
  currentUser: State['currentUser']
  patron: State['patron']
}

const DigitalMembershipCardContent: Cell<DigitalMembershipCardDetailProps> = ({
  loading,
  artist,
  currentUser,
  patron,
}) => {
  const { t } = useTranslation('digitalMembershipCardDetail')

  if (loading) {
    return (
      <Box sx={{ px: 5, width: '100%', maxWidth: 400 }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          sx={{ borderRadius: 3 }}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ px: 5, width: '100%', maxWidth: 600 }}>
      <DigitalMembershipCard
        cardDesignPath={
          artist?.patron?.userDigitalMembershipCard?.cardDesignPath
        }
        textColor={artist?.patron?.userDigitalMembershipCard?.textColor}
        displayAreaColor={
          artist?.patron?.userDigitalMembershipCard?.displayAreaColor ||
          undefined
        }
        displayName={currentUser?.displayName}
        membershipNumber={
          artist?.patron?.formattedMembershipNumber || undefined
        }
        planInterval={patron?.plan?.interval as MembershipInterval}
        joinDate={artist?.patron?.createdAt}
        favoriteMemberCreatedAt={artist?.patron?.favoriteMember?.createdAt}
        loading={loading}
      />

      {/* アーティスト名、プラン名、メンバー限定アイコン */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
          }}
        >
          {artist?.displayName || ''}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {patron?.title || ''}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <Icons.MembersOnly
            fontSize="medium"
            sx={{ color: 'primary.main', width: 12, height: 12 }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: '12px',
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {t('membersOnly')}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
